import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { config } from './config/config';

// Reduce console noise during development.
// Many parts of the app emit verbose debug logs; keep warnings/errors visible.
if (process.env.NODE_ENV === 'development' && config.console?.suppressLogs) {
  const noop = () => {};

  // eslint-disable-next-line no-console
  console.log = noop;
  // eslint-disable-next-line no-console
  console.info = noop;
  // eslint-disable-next-line no-console
  console.debug = noop;
}

// ReactQuill (react-quill@2) still uses findDOMNode internally in some paths.
// React 18 prints a deprecation warning that is noisy in development.
// Filter only this specific warning to keep the console usable.
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line no-console
  const originalError = console.error;
  // eslint-disable-next-line no-console
  const originalWarn = console.warn;

  const shouldIgnore = (args: unknown[]) => {
    const stringParts = args.filter((a) => typeof a === 'string') as string[];
    if (stringParts.length === 0) return false;

    if (stringParts.some((s) => s.includes('quill:toolbar ignoring attaching to disabled format fontDecrease'))) {
      return true;
    }

    // React's deprecation message can come as multiple args (message + component stack).
    const hasFindDomNodeWarning = stringParts.some((s) => s.includes('findDOMNode is deprecated'));
    if (!hasFindDomNodeWarning) return false;

    const looksLikeQuillStack = stringParts.some((s) => s.includes('ReactQuill') || s.includes('react-quill'));

    // If we can positively identify it's from ReactQuill, filter it.
    if (looksLikeQuillStack) return true;

    // Fallback: filter the findDOMNode deprecation warning itself (still very specific),
    // since it is currently expected noise from 3rd-party libs.
    return true;
  };

  // eslint-disable-next-line no-console
  console.error = (...args: any[]) => {
    if (shouldIgnore(args)) return;
    originalError(...args);
  };

  // eslint-disable-next-line no-console
  console.warn = (...args: any[]) => {
    if (shouldIgnore(args)) return;
    originalWarn(...args);
  };
}

// Global styles
const manropeUrl = `${process.env.PUBLIC_URL}/fonts/Manrope-Regular.ttf`;

const globalStyles = `
  @font-face {
    font-family: 'Manrope';
    src: url('${manropeUrl}') format('truetype');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    line-height: 1.5;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
  }

  /* Search input placeholder */
  #search-input::placeholder {
    color: #5A5A5A;
    opacity: 1;
  }

  /* Global checkbox sizing (design spec) */
  input[type='checkbox'] {
    width: 18px !important;
    height: 18px !important;
    min-width: 18px;
    min-height: 18px;
    accent-color: #2F58F2;
  }

  /* Global radio sizing (design spec) */
  input[type='radio'] {
    width: 18px !important;
    height: 18px !important;
    min-width: 18px;
    min-height: 18px;
    accent-color: #2F58F2;
  }

  /* Alert flame flicker (overlay only, base stays visible) */
  @keyframes alertFlameFlicker {
    0% {
      opacity: 0.15;
      transform: scale(0.98);
    }
    50% {
      opacity: 1;
      transform: scale(1.08);
    }
    100% {
      opacity: 0.2;
      transform: scale(1);
    }
  }

  .alert-flame-flicker {
    animation: alertFlameFlicker 1.8s ease-in-out infinite;
    transform-origin: center;
    transform-box: fill-box;
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  ::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #555;
  }

  /* Dark mode scrollbar */
  @media (prefers-color-scheme: dark) {
    ::-webkit-scrollbar-track {
      background: #374151;
    }

    ::-webkit-scrollbar-thumb {
      background: #6B7280;
    }

    ::-webkit-scrollbar-thumb:hover {
      background: #9CA3AF;
    }
  }

  /* Vertical slider styles */
  .vertical-slider {
    -webkit-appearance: none;
    appearance: none;
  }

  .vertical-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    height: 16px;
    width: 16px;
    border-radius: 50%;
    background: #ffffff;
    border: 2px solid #3b82f6;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
  }

  .vertical-slider::-webkit-slider-thumb:hover {
    background: #3b82f6;
    transform: scale(1.1);
  }

  .vertical-slider::-moz-range-thumb {
    height: 16px;
    width: 16px;
    border-radius: 50%;
    background: #ffffff;
    border: 2px solid #3b82f6;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
  }

  .vertical-slider::-moz-range-thumb:hover {
    background: #3b82f6;
    transform: scale(1.1);
  }

  /*
    Some charts are wrapped in a focusable div (role="button" + tabIndex=0) so they can
    be opened via keyboard. After closing an expanded chart with Esc, browsers can apply
    a default focus-visible outline (often black), which looks like an unintended border.
  */
  [role='button'][tabindex='0']:focus,
  [role='button'][tabindex='0']:focus-visible {
    outline: none;
  }

  /* Quill picker dropdowns (color/background) should not be clipped */
  .ql-toolbar {
    overflow: visible !important;
  }

  .ql-container {
    overflow: visible !important;
  }

  .ql-toolbar .ql-picker-options {
    z-index: 10002 !important;
  }
`;

// Inject global styles
const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = globalStyles;
document.head.appendChild(styleSheet);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <App />
);



